/**
 * The JSON wire format of a google.protobuf.Duration.
 *
 * https://developers.google.com/protocol-buffers/docs/proto3#json
 * Duration string e.g. "1.000340012s", "1s".
 *
 * Generated output always contains 0, 3, 6, or 9 fractional digits, depending on required precision,
 * followed by the suffix "s". Accepted are any fractional digits (also none) as long as they fit into
 * nano-seconds precision and the suffix "s" is required.
 */
export type duration = string & { durationBrand: 'brand' } | { durationBrand: 'brand' };

// A precise representation of google.protobuf.Duration.
export interface Duration {
  // Signed seconds of the span of time. Must be from -315,576,000,000
  // to +315,576,000,000 inclusive. Note: these bounds are computed from:
  // 60 sec/min * 60 min/hr * 24 hr/day * 365.25 days/year * 10000 years
  seconds: number

  // Signed fractions of a second at nanosecond resolution of the span
  // of time. Durations less than one second are represented with a 0
  // `seconds` field and a positive or negative `nanos` field. For durations
  // of one second or more, a non-zero value for the `nanos` field must be
  // of the same sign as the `seconds` field. Must be from -999,999,999
  // to +999,999,999 inclusive.
  nanos: number
}

export namespace Duration {
  export const nil = '' as duration
  export const zero = '0s' as duration

  // TODO: tighten this up to specify max digits?
  const reFmt = /^-?\d+(.\d+)?s$/

  // Convert a floating point number of seconds to a duration.
  export function fromSeconds(seconds: number): duration {
    // 9 fractional digits per spec proto-json spec.
    let vStr = seconds.toLocaleString('fullwide', {
      useGrouping: false,
      maximumSignificantDigits: 21,
    });
    let i = vStr.indexOf('.');
    let prefix = vStr;
    let trailing = '';
    if (i != -1) {
      prefix = vStr.slice(0, i);
      trailing = vStr.slice(i + 1);
      trailing = trailing.slice(0, 9);
    }

    if (trailing.length) {
      // some number of fractional digits, thus needs a decimal and possibly some padding to reach a multiple of three (matching proto spec)
      while (trailing.length % 3 != 0) {
        trailing += '0';
      }
      return `${prefix}.${trailing}s` as duration;
    } else {
      // no fractional digits
      return `${prefix}s` as duration;
    }
  }

  // Parse a duration into a floating point number of seconds.  The returned value
  // may lose precision, since a float cannot precisely hold the entire range of Duration.
  // Use toProto instead if you need an exact representation.  Returns NaN if the input
  // cannot be parsed.
  export function toSeconds(dur: duration | null | undefined): number {
    if (!dur) {
      return 0
    }

    let d = dur as string
    if (!d.match(reFmt)) {
      return NaN
    }

    let dp = parse(d)
    return dp.seconds + dp.nanos / 1000000000
  }

  // Convert an integral number of millis into a duration. Any fractional millis are truncated.
  export function fromMillis(millis: number): duration {
    millis = Math.trunc(millis)
    if (millis == 0) {
      return '0s' as duration
    } else if (millis > 0 && millis < 1000) {
      return '0.' + millis.toString().padStart(3, '0') + 's' as duration
    } else if (millis < 0 && millis > -1000) {
      return '-0.' + (-millis).toString().padStart(3, '0') + 's' as duration
    }

    let str = millis.toString()
    let sec = str.substring(0, str.length - 3)
    let nan = str.substring(str.length - 3, str.length)
    if (nan == '000') {
      return sec + 's' as duration
    } else {
      return sec + '.' + nan + 's' as duration
    }
  }

  // Parse a duration into an integral number of milliseconds.  The returned value
  // may lose precision, since a float cannot precisely hold the entire range of Duration.
  // Any fractional millis are truncated. Returns NaN if the input cannot be parsed.
  export function toMillis(dur: duration | null | undefined): number {
    if (!dur) {
      return 0
    }

    let d = dur as string
    if (!d.match(reFmt)) {
      return NaN
    }

    let dp = parse(d)
    return dp.seconds * 1000 + Math.trunc(dp.nanos / 1000000)
  }

  // Convert a Duration to a duration.
  export function fromProto(dur: Duration | null): duration | null {
    if (!dur) {
      return null
    }
    if (dur.nanos == 0) {
      return (dur.seconds.toString() + 's') as duration
    }

    let nanos = dur.nanos
    if (nanos < 0 && dur.seconds < 0) {
      // sign must match seconds, negate for representation
      // throw an error here if there's a mismatch?
      nanos = -nanos
    }

    let tail = nanos.toString()
    tail = tail.padStart(9, '0')
    while (tail.endsWith('000')) {
      tail = tail.substring(0, tail.length - 3)
    }
    return (dur.seconds.toString() + '.' + tail + 's') as duration
  }

  // Parse a duration into a Duration, which exactly represents the value of the input
  // Returns null if the input cannot be parsed.
  export function toProto(dur: duration | null): Duration | null {
    if (!dur) {
      return null
    }

    let d = dur as string
    if (!d.match(reFmt)) {
      return null
    }

    return parse(d)
  }

  function parse(d: string): Duration {
    let pos = d.indexOf(".")
    if (pos < 0) {
      return { seconds: parseInt(d.slice(0, d.length - 1), 10), nanos: 0 }
    } else {
      let nanos = parseInt(d.slice(pos + 1, d.length - 1).substring(0, 9).padEnd(9, '0'), 10);
      let seconds = parseInt(d.slice(0, pos), 10);
      if (seconds < 0) {
        nanos = -nanos
      }
      return {
        seconds: seconds,
        nanos: nanos,
      }
    }
  }
}
